// ─── Le ore libere di un giorno, messe in griglia (voce 14) ──────────────────
//
// 🗣️ 23/09/2026, guardando l'anteprima dal telefono: *«così è proprio brutto
// utilizzarlo da cellulare, trova una maniera più bella e facile»*. Mockup
// approvato: `mockup/maestro-scelta-ora-mockup.html`.
//
// ⭐⭐ **PRIMA L'ORA, POI IL CAMPO.** Il server risponde per campo (una fascia
// per campo, con dentro i suoi inizi); qui le stesse risposte si rigirano per
// ora: «alle 19:00 sono liberi il Campo 1, il 3 e il 4». A bordo campo si cerca
// *quando*, e il campo quasi sempre va bene il primo che c'è.
//
// ⚖️ **Sta in un file suo, fuori dalla schermata**: sono tre regole pure (quali
// ore, quali campi, quando un'ora si accavalla con una lezione sua), e così una
// prova le può sabotare senza un browser (⇒ `orari.test.ts`).

import { minutiDaOra, oraDaMinuti, type FasciaDelMioGiorno } from '@pmo/shared';

/** Una lezione sua di quel giorno: l'unica cosa occupata di cui si sa il nome. */
export interface MiaDelGiorno {
  lezioneId: string;
  dalle: string;
  alle: string;
  courtName: string;
}

/** Un'ora che si può prendere, con i campi liberi a quell'ora. */
export interface OrarioLibero {
  time: string;
  /** In ordine di nome, e il primo è quello che il foglio propone. */
  campi: Array<{ courtId: string; courtName: string }>;
  /**
   * ⚠️ La lezione sua con cui quest'ora si accavalla, o `null`. Non la toglie:
   * può essere una cosa voluta (un'altra lezione su un altro campo, un cambio
   * di programma). La **dice**, così non la si scopre dopo.
   */
  accavallata: MiaDelGiorno | null;
}

/** 🔢 «Campo 10» dopo «Campo 9», non dopo «Campo 1». */
const perNome = (a: string, b: string): number => a.localeCompare(b, 'it', { numeric: true });

/**
 * Le sue lezioni del giorno, una volta sola ciascuna.
 *
 * 🔒 Si riconoscono da `lezioneId`: il server lo mette **solo** sulle sue
 * (⇒ `api/modules/maestro/fasce.ts`). Un'ora occupata da un altro non ha nome,
 * e qui non se ne inventa uno.
 */
export function leMieDelGiorno(fasce: readonly FasciaDelMioGiorno[]): MiaDelGiorno[] {
  const viste = new Map<string, MiaDelGiorno>();
  for (const f of fasce) {
    if (f.lezioneId === null || viste.has(f.lezioneId)) continue;
    viste.set(f.lezioneId, {
      lezioneId: f.lezioneId,
      dalle: f.startsAtTime,
      alle: f.endsAtTime,
      courtName: f.courtName,
    });
  }
  return [...viste.values()].sort(
    (a, b) => a.dalle.localeCompare(b.dalle) || perNome(a.courtName, b.courtName),
  );
}

/**
 * ⭐ Le ore libere del giorno, **per ora**.
 *
 * 🚨 Gli inizi NON si inventano: sono quelli che il server ha messo dentro le
 * fasce libere, che già tengono conto del passo del circolo, delle sue ore e
 * di adesso. ⇒ Un'ora compare solo se almeno un campo la offre davvero; un'ora
 * piena **non** si disegna, perché per disegnarla bisognerebbe indovinare il
 * passo del circolo (con fasce da 90′ una griglia a mezz'ore mostrerebbe
 * «pieno» su orari che non esistono).
 */
export function orariLiberi(fasce: readonly FasciaDelMioGiorno[], durataMinuti: number): OrarioLibero[] {
  const perOra = new Map<string, Array<{ courtId: string; courtName: string }>>();
  for (const f of fasce) {
    if (f.stato !== 'libera') continue;
    for (const t of f.inizi) {
      const campi = perOra.get(t) ?? [];
      if (!campi.some((c) => c.courtId === f.courtId))
        campi.push({ courtId: f.courtId, courtName: f.courtName });
      perOra.set(t, campi);
    }
  }
  const mie = leMieDelGiorno(fasce);
  return [...perOra.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([time, campi]) => {
      const da = minutiDaOra(time);
      const a = da + durataMinuti;
      return {
        time,
        campi: campi.sort((x, y) => perNome(x.courtName, y.courtName)),
        // Si accavalla se comincia prima che la sua finisca e finisce dopo che
        // la sua comincia: due lezioni che si toccano (10:00 e 10:00) no.
        accavallata: mie.find((m) => minutiDaOra(m.dalle) < a && minutiDaOra(m.alle) > da) ?? null,
      };
    });
}

/** Le tre parti della giornata in cui si divide la griglia. */
export const MOMENTI = ['Mattina', 'Pomeriggio', 'Sera'] as const;
export type Momento = (typeof MOMENTI)[number];

/** 🌅 Prima delle 13 è mattina, dalle 18 è sera. */
export function momentoDi(time: string): Momento {
  const m = minutiDaOra(time);
  if (m < 13 * 60) return 'Mattina';
  if (m < 18 * 60) return 'Pomeriggio';
  return 'Sera';
}

/** Dove finisce una lezione che comincia a `time` e dura `durataMinuti`. */
export function fineDi(time: string, durataMinuti: number): string {
  return oraDaMinuti(minutiDaOra(time) + durataMinuti);
}
